import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowRight, ChevronDown, ContactRound, Menu, ShoppingBag, Sparkles, Users, X } from 'lucide-react';
import { Wordmark } from '@/components/Wordmark';
import { useAuth } from '@/stores/auth';

const PRODUCTS = [
  { to: '/features/cards', label: 'Digital cards', text: 'The right introduction for every side of you.', icon: ContactRound },
  { to: '/features/shop', label: 'Your shop', text: 'Turn “I love that” into your next order.', icon: ShoppingBag },
  { to: '/features/leads', label: 'Leads & contacts', text: 'Remember the person. Keep the conversation.', icon: Users },
  { to: '/features/grow', label: 'Follow-ups & insights', text: 'Stay in touch and see what works.', icon: Sparkles },
];

export function MegaNav() {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLElement>(null);
  const desktopToggle = useRef<HTMLButtonElement>(null);
  const mobileToggle = useRef<HTMLButtonElement>(null);
  const user = useAuth(s => s.user);
  const { pathname, hash } = useLocation();
  useEffect(() => setOpen(false), [pathname, hash]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      (window.matchMedia('(min-width:768px)').matches ? desktopToggle : mobileToggle).current?.focus();
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [open]);
  const links = <>{PRODUCTS.map(({ to, label, text, icon: Icon }) => <NavLink key={to} to={to} className="mega-product" onClick={() => setOpen(false)}><span className="mega-product__icon"><Icon size={22}/></span><span><strong>{label}</strong><small>{text}</small></span><ArrowRight size={16}/></NavLink>)}</>;
  return <header ref={root} className="mega-header" onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }}>
    <a className="mega-skip" href="#main-content">Skip to content</a>
    <div className="mega-bar">
      <Link to="/" aria-label="Haphe home"><Wordmark className="text-xl"/></Link>
      <nav aria-label="Main navigation" className="mega-desktop">
        <button ref={desktopToggle} className="mega-trigger" aria-expanded={open} aria-controls="haphe-mega-menu" onClick={() => setOpen(!open)}>Explore Haphe <ChevronDown size={15}/></button>
        <NavLink to="/pricing">Pricing</NavLink>
        <Link to="/features/cards#how-it-works">How it works</Link>
      </nav>
      <div className="mega-account">{!user && <Link to="/login">Log in</Link>}<Link className="mega-cta" to={user ? '/app' : '/signup'}>{user ? 'Open Haphe' : 'Get your card, free'}<ArrowRight size={15}/></Link></div>
      <button ref={mobileToggle} className="mega-mobile-toggle" aria-label={open ? 'Close menu' : 'Open menu'} aria-expanded={open} aria-controls="haphe-mega-menu" onClick={() => setOpen(!open)}>{open ? <X/> : <Menu/>}</button>
    </div>
    <div id="haphe-mega-menu" className="mega-panel" hidden={!open}>
      <nav className="mega-panel__inner" aria-label="Explore Haphe">
        <div className="mega-products"><p className="mk-kicker">One connection. So many possibilities.</p><div className="mega-products__grid">{links}</div></div>
        <aside className="mega-feature"><span className="mk-kicker">Start with a hello</span><strong>A little card.<br/>A whole lot of you.</strong><p>Your photo, your links, your story. Share it with a scan, a tap, or a link.</p><Link to="/features/cards#your-cards" onClick={() => setOpen(false)}>Meet your next card <ArrowRight size={16}/></Link></aside>
        <div className="mega-bottom"><span>No app needed to view your card.</span><Link to="/features/cards#how-it-works" onClick={() => setOpen(false)}>See how it works <ArrowRight size={14}/></Link><Link to="/pricing" onClick={() => setOpen(false)}>Compare plans <ArrowRight size={14}/></Link><Link className="mega-mobile-account" to={user ? '/app' : '/login'}>{user ? 'Open Haphe' : 'Log in / Get started'}</Link></div>
      </nav>
    </div>
  </header>;
}
